<template>
  <div class="relative">
        <label
            v-if="label"
            :for="id ? id : name"
            class="block font-bold text-gray-700 text-sm"
        >{{ label }}</label>
        <p class="small text-gray-500 text-xs mb-0.5" v-if="$slots.description">
            <slot name="description" />
        </p>
        <label
            v-if="type == 'color'"
            :for="id ? id : name"
            class="flex items-center"
        >
            <div class="w-10 h-10 rounded-full border-2 shadow-md" :style="'background:' + (modelValue ? modelValue : '#fff')"></div>
            <span class="ml-1 font-bold" v-html="modelValue ? modelValue : '#fff'"></span>
        </label>

        <label v-if="type == 'file'" :for="id ? id : 'file'" class="w-full h-full p-2 border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-md text-gray-300 hover:text-gray-400 flex justify-center items-center cursor-pointer transition duration-300">
            <input type="file" class="hidden h-0 w-0" :id="id ? id : 'file'" :multiple="multiple" @change="updateValue($event)"/>
            <SvgIcon
                name="plus-circle"
                width="36"
                height="36"
            />
        </label>
        <textarea
            v-else-if="type == 'textarea'"
            :ref="el => { input = el }"
            class="w-full py-2 px-3 cursor-pointer text-left bg-white border shadow-sm focus:outline-none focus:ring-1 focus:ring-opacity-75 focus:ring-white  focus:ring-offset-2 text-sm focus:shadow-outline"
            :rows="maxLines ? maxLines : rows"
            :class="{
                [inputClass]: inputClass,
                [rounded]: rounded,
                ['bg-gray-400 opacity-50'] : disabled,
                ['bg-gray-200 opacity-75'] : readOnly,
                ['overflow-hidden'] : modelValue && autoHeight,
                ['border-red-500 focus:border-red-600 focus:ring-offset-red-200 is-invalid'] : errorMessage,
                ['border-gray-300 focus:border-gray-400 focus:ring-offset-gray-300'] : !errorMessage,
            }"

            @input="updateValue($event)"
            @keypress.enter="checkLine"
            :disabled="disabled"
            :value="modelValue ? modelValue : null"
            :maxlength="maxLength"
            @change="changeEvent($event)"

            :style="(modelValue && autoHeight) ? 'height:' + resizeTextarea : 0"
        ></textarea>

        <QuillEditor
            class="rounded-b-md shadow-md bg-white z-10 relative"
            style="font-family: 'Nunito'"
            :style="[
                inputStyle ? inputStyle : ''
            ]"
            v-else-if="type == 'quill'"
            theme="snow"
            :toolbar="[
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote'],

                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'indent': '-1'}, { 'indent': '+1' }, { 'align': [] }],

                [{ 'color': [] }],
                ['link', 'image', 'video'],

                ['clean']
            ]"
            v-model:content="modelValue"
            contentType="html"
            @textChange="updateValue($event)"

        />
        <input
            :ref="el => { input = el }"
            v-else
            :id="id ? id : name"
            :type="type"
            :step="step"
            :min="min"
            :max="max"
            :pattern="pattern"
            :autocomplete="autocomplete"
            :placeholder="placeholder"
            :class="{
                [inputClass]: inputClass,
                [rounded]: rounded,
                ['py-1 px-2'] : size == 'small',
                ['py-2 px-3'] : size == 'default',
                ['bg-gray-400 opacity-50'] : readOnly || disabled,
                ['border-red-500 focus:border-red-600 focus:ring-offset-red-200 is-invalid'] : errorMessage,
                ['border-gray-300 focus:border-gray-400 focus:ring-offset-gray-300'] : !errorMessage,
                ['w-full cursor-pointer text-left bg-white border shadow-sm focus:outline-none focus:ring-1 focus:ring-opacity-75 focus:ring-white  focus:ring-offset-2 text-sm focus:shadow-outline'] : type != 'color',
                ['overflow-hidden w-0 h-0 absolute bottom-0'] : type == 'color'
            }"
            :readonly="readOnly"
            :disabled="disabled"
            @input="updateValue($event)"
            @change="changeEvent($event)"
            :value="modelValue ? modelValue : null"
        />
        <div class="text-red-500 text-xs font-bold" v-if="errorMessage">{{ Array.isArray(errorMessage) ? errorMessage[0] : errorMessage }}</div>
        <p class="small text-gray-500 text-xs text-right" v-if="maxLength">{{__(':value character(s) left', { value: calculateLength})}}</p>
        <slot name="addons" />
  </div>
</template>

<script>
import { ref } from '@vue/reactivity';
import { computed, nextTick, onMounted, watch } from '@vue/runtime-core';
import { QuillEditor } from '@vueup/vue-quill'
import SvgIcon from 'UI/SvgIcon';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

import { usePage } from '@inertiajs/inertia-vue3';

export default {
    components: { QuillEditor, SvgIcon },
    props: {
        label: String,
        placeholder: String,
        inputClass: String,
        inputStyle: String,
        type: {
            type: String,
            default: 'text'
        },
        id: String,
        name: String,
        pattern: String,
        readOnly: [Boolean],
        disabled: [Boolean],
        rows: [String, Number],
        maxLength: [String, Number],
        min: [Number, String],
        max: [Number, String],
        autocomplete: String,
        enterAction: Boolean,
        multiple: {
            type: Boolean,
            default: false
        },
        step: String,
        size: {
            type: String,
            default: 'default'
        },
        autoHeight: Boolean,
        value: [String, Number, Boolean],
        data: [Array, Object],
        modelValue: {
            type:[String, Number, Boolean, Array, Object],
            default: ''
        },
        maxLines: [String, Number],
        error: [String, Array, Boolean],
        rounded: {
            type: String,
            default: 'rounded-md'
        },
    },
    emits: ['update:modelValue', 'update', 'change'],

    setup(props, { emit }) {
        const calculateLength = ref(props.maxLength);
        const errorMessage = ref(props.error);

        const
            updateValue = (ev) => {
                if (props.type == 'quill') {
                    emit('update:modelValue', props.modelValue);
                    emit('update');
                }
                else {
                    emit('update:modelValue', props.type == 'file' ? (props.multiple ? ev.target.files : ev.target.files[0]) : ev.target.value);
                    emit('update', props.type == 'file' ? (props.multiple ? ev.target.files : ev.target.files[0]) : ev.target.value);
                    if(props.type != 'file') updateMaxLength(ev.target.value);
                    errorMessage.value = null;
                }
            },
            updateMaxLength = (value) => {
                if(props.maxLength){
                    if(calculateLength.value > 0) calculateLength.value = props.maxLength - value.length;
                    else if(calculateLength.value <= props.maxLength) calculateLength.value = props.maxLength + value.length;
                }
            },
            changeEvent = (ev) => {
                if(props.type == 'file') emit('change', ev.target.files);
                else emit('change', ev);
            };
        function checkLine(ev){
            if(props.enterAction){
                ev.preventDefault();
                emit('change', ev);
            }
            else {
                let lines = ev.target ? ev.target.value.split('\n') : ev.value.split('\n');

                if(lines.length >= props.maxLines) {
                    if(ev.target) ev.preventDefault();

                    errorMessage.value = app.__('You can add up to :value line (s).', {value : props.maxLines} );

                    setTimeout(() => {
                        errorMessage.value = null;
                    }, 4000)
                }
            }
        };

        const input = ref(null);

        onMounted(() => {
            if(props.type != 'quill' && input.value)
            updateMaxLength(input.value.value);
        })

        const resizeTextarea = computed(() => {
            if(props.modelValue && props.autoHeight && input.value && input.value.scrollHeight > 60 )
                return (input.value.scrollHeight) + 'px';
        });

        watch( () => usePage().props.value.errors, (error, oldError) => {
            if(props.name || props.id) errorMessage.value = error[props.name ? props.name : props.id];
            else errorMessage.value = null;

            nextTick(() => {
                if($('.is-invalid').length && $('.is-invalid')[0] == input.value) {

                    window.scrollTo({
                        top: $(input.value).offset().top - 120,
                        behavior: 'smooth'
                    });
                }
            })
        })
        // watch(() => props.modelValue, (val, oldVal) => {
        //     if(errorMessage.value && val != oldVal) errorMessage.value = null;
        // })

        watch(() => props.maxLines, (val, oldVal) => {
            checkLine(input.value);
        })


        return {
            updateValue,
            input,
            errorMessage,

            resizeTextarea,

            calculateLength,
            checkLine,
            changeEvent
        }
    },
}
</script>

<style lang="postcss">
.ql-toolbar {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
        --tw-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
    /* max-height: 95px;
    overflow: auto; */
}

.ql-picker-options {
    z-index: 20 !important;
}
.ql-container {
    @apply text-base !important;
}
/* border-radius: 0.375rem; */
</style>
